import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import ApiServices from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosClient = AxiosApiCall();

export const useAdminPermissions = (adminId, options = {}) => {
  return useQuery({
    queryKey: ["adminPermissions", adminId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getAdminPermissionsByid, {
        params: { admin_id: adminId },
      });
      return response.data?.data || [];
    },
    enabled: !!adminId,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

export const usePermissionsList = (options = {}) => {
  return useQuery({
    queryKey: ["permissionsList"],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getPermissions);
      return response.data?.data || [];
    },
    staleTime: 1000 * 60 * 15,
    ...options,
  });
};

export const useAssignPermissions = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.post(ApiServices.assignPermissions, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["adminPermissions"] });
      queryClient.invalidateQueries({ queryKey: ["permissionsList"] });
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to assign permissions";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useRemovePermissions = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.post(ApiServices.removePermissionsFromAdmin, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["adminPermissions"] });
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to remove permissions";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useUpdatePermissions = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.put(ApiServices.updatePermissions, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["permissionsList"] });
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to update permissions";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};
